<template>
    <group-report-template  v-model:current-report-template-id="currentReportTemplateId"
                            :template-id="templateId"
                            v-model:for-group="forGroup"
                            v-model:interval="interval"
                            v-model:ran-report="reportSuccessfullySent"
                            v-model:reload-data="reloadTable"
                            v-model:running-errors="errorSending"
                            v-model:running-report="runningReport"
                            v-model:settings="settings"
                            :can-edit="permissions.can('manage_room_condition_report')"
                            :users="users"
                            :default-settings="defaultSettings"
                            :prepare-settings-function="prepareSettingsFunction"
                            :hide-title="true"
                            :use-settings-drawer="true"
                            :use-template-cards="true"
                            :use-group-reporting-option="groupReportingEnabled"
                            :has-settings="hasManualRunSettings"
                            :report-icon="settingsSchema.icon"
                            :schema="settingsSchema"
                            report-name="room-condition-report"
                            v-model:display-section="displaySection"
                            @useStandardReporting="setUseStandardReporting"
                            @reset-settings="resetSelectedZones"
    >

        <template   #create-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="true"
                                    :timezone="timezone"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #edit-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="displaySection === 'show-template' || useStandardReporting"
                                    :timezone="timezone"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #run-options="props">
            <!-- Room Condition selects zones (not meters) over an interval-derived
                 window, so it uses the shared IntervalSelection in zone mode with a
                 visible anchor date. The send-times schedule is template-only and
                 lives in the settings drawer, not here. -->
            <interval-selection picker-type="zones"
                                :show-anchor-date="true"
                                v-model:end-of-interval="endOfInterval"
                                v-model:interval="interval"
                                v-model:selected-zones="selectedZones"
                                :zones="zonesByGroup[props.groupId]"
                                :zone-tree="zoneTree"
                                :footer-mode="props.footerMode ?? 'none'"
                                :max-date="maxDate"
                                :min-date="minDate"
                                :is-running="runningReport"
                                :ran-report="reportSuccessfullySent"
                                :running-errors="errorSending"
                                :timezone="timezone"
                                @run-report="runReport(currentReportTemplateId)"
            ></interval-selection>
        </template>
    </group-report-template>
</template>

<script>
import {ref} from 'vue'

import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "../../mixins/ajaxTableMixin";
import {useGroupReport} from "@/composables/useGroupReport";

import GroupReportTemplate from "./GroupReportTemplate";
import IntervalSelection from "./IntervalSelection";
import ReportSettingsPanel from "./settings/ReportSettingsPanel.vue";
import roomConditionSettingsSchema from "./settings/schemas/room-condition";
import {normalizeSendTime} from "./settings/schemas/room-condition-send-times";

export default {
    name: "GroupRoomConditionReport",
    mixins: [SonicuCoreMixin, ajaxTableMixin],
    components: {
        GroupReportTemplate,
        IntervalSelection,
        ReportSettingsPanel,
    },
    props: {
        // null on the report page; 'new' or a group report id on its New or
        // Edit page.
        templateId: {
            type: [String, Number],
            default: null,
        },
        zones: {
            type: Object,
            default() {
                return {}
            },
        },
        zonesByGroup: {
            type: Object,
            default() {
                return {}
            },
        },
        zoneTree: {
            type: Array,
            default() {
                return []
            },
        },
        minDate: {
            type: [Date, String],
        },
        maxDate: {
            type: [Date, String],
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            }
        },
        users: {
            type: Object,
            default() {
                return {}
            }
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
    },
    setup(props) {
        // Room Condition runs on an interval-derived window over selected zones.
        // Its interval default is daily (not the composable's monthly), and the
        // selected zones are its own run state. sendTimes (the schedule) is the
        // only drawer setting and is template-only, so extraParams posts just the
        // zones the run form picked (interval/end come from the base params).
        const selectedZones = ref([])

        const report = useGroupReport(roomConditionSettingsSchema, {
            interval: 'daily',
            defaultSettings: props.defaultSettings,
            extraParams: () => ({
                selected_zones: selectedZones.value,
            }),
        })

        return {
            ...report,
            selectedZones,
        }
    },
    data() {
        return {
            prepareSettingsFunction: null,
        }
    },
    created() {
        this.prepareSettingsFunction = (settings) => {
            // The backend has stored sendTimes as full datetime strings or hours
            // over time; the `time` field reads/writes "HH:mm". Normalise whatever
            // is stored so an existing template edits correctly and a new one
            // saves "HH:mm". Empty/unset means no schedule yet.
            if (Array.isArray(settings['sendTimes'])) {
                settings['sendTimes'] = settings['sendTimes']
                    .map((value) => normalizeSendTime(value, this.timezone))
                    .filter((value) => value !== null)
            }

            return settings
        }
    },
    computed: {
        settingsSchema() {
            return roomConditionSettingsSchema
        },
    },
    methods: {
        resetSelectedZones() {
            this.selectedZones = []
        },
    },
}
</script>
